import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { EpaysageAltPrintDataFields } from '../hooks/use-print-data-alt';



import {
  pdfStyles,
  PDFHeader,
  PDFInfoField,
  PDFSectionHeader,
  PDFRiskIndicator,
  formatRiskLabel,
  formatValueWithSujetLabel,
  convertImageUrlForPDF,
} from '../../../shared';
import { formatDate } from '@repo/ui/lib/utils';


export const isNotEmpty = (value?: string | number | null): boolean => {
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  return value !== null && value !== undefined && value !== ''
};

// Essence Section Component for PDF
function EssencePDFSection({
  essence,
  essenceIndex,
  sectionTitle,
  essenceName,
}: {
  essence: any;
  essenceIndex: number;
  sectionTitle: string;
  essenceName: string;
}) {

  return (
    <View>
      <PDFSectionHeader title={`${sectionTitle}`} variant="secondary" />
      <View style={pdfStyles.grid2}>
        <View style={pdfStyles.column}>
          <PDFInfoField label={sectionTitle} value={essenceName} layout="row" />
          <PDFInfoField
            label="Nbr d'arbres"
            value={
              essence.count_subject
                ? formatValueWithSujetLabel(essence.count_subject)
                : ''
            }
            layout="row"
          />
        </View>
        <View style={pdfStyles.column}>
          <PDFInfoField
            label="Diamètre moyen"
            value={essence.diametre}
            layout="row"
          />
          <PDFInfoField
            label="Hauteur moyenne"
            value={essence.hauteur}
            layout="row"
          />
        </View>
      </View>

      {/* Images */}
      {(essence.img1 || essence.img2) && (
        <View style={pdfStyles.grid2}>
          {essence.img1 && (
            <View style={pdfStyles.photoContainerSmall}>
              <Image
                src={convertImageUrlForPDF(essence.img1, { width: 700 })}
                style={pdfStyles.photoSmall}
              />
            </View>
          )}
          {essence.img2 && (
            <View style={pdfStyles.photoContainerSmall}>
              <Image
                src={convertImageUrlForPDF(essence.img2, { width: 700 })}
                style={pdfStyles.photoSmall}
              />
            </View>
          )}
        </View>
      )}

      <PDFSectionHeader title="DIAGNOSTIC VISUEL" variant="secondary" />
      <View style={pdfStyles.grid2}>
        <View style={pdfStyles.column}>
          <PDFInfoField
            label="Stade de développement"
            value={essence.stade_dev}
            layout="row"
          />
          <PDFInfoField
            label="État physiologique"
            value={essence.etat_san_general}
            layout="row"
          />
        </View>
        <View style={pdfStyles.column}>
          <PDFInfoField
            label="Port général"
            value={essence.port_arbre}
            layout="row"
          />
          <PDFInfoField
            label="Critères particuliers"
            value={essence.critere
            }
            layout="row"
          />
          <View style={pdfStyles.infoFieldRow}>
            <Text style={pdfStyles.infoLabel}>Risque de rupture générale essence {essenceIndex + 1} : </Text>
            <PDFRiskIndicator risk={essence.risque} showLabel={true} size="small" />
          </View>
        </View>
      </View>

      <Text style={pdfStyles.travauxTitle}>Travaux :</Text>
      <View style={pdfStyles.grid2}>
        <View style={pdfStyles.column}>
          {isNotEmpty(essence.travaux_aucun) && (
            <PDFInfoField label="- Aucun travaux" value={formatValueWithSujetLabel(essence.travaux_aucun)} layout="row" />
          )}
          {isNotEmpty(essence.travaux_abattage) && (
            <PDFInfoField label="- Abattage" value={formatValueWithSujetLabel(essence.travaux_abattage)} layout="row" />
          )}
          {isNotEmpty(essence.travaux_demontage) && (
            <PDFInfoField label="- Démontage" value={formatValueWithSujetLabel(essence.travaux_demontage)} layout="row" />
          )}
          {isNotEmpty(essence.travaux_mise_en_securite) && (
            <PDFInfoField
              label="- Mise en sécurité"
              value={formatValueWithSujetLabel(essence.travaux_mise_en_securite)}
              layout="row"
            />
          )}
          {isNotEmpty(essence.travaux_taille_sanitaire) && (
            <PDFInfoField
              label="- Taille sanitaire"
              value={formatValueWithSujetLabel(essence.travaux_taille_sanitaire)}
              layout="row"
            />
          )}
          {isNotEmpty(essence.travaux_couple_selective) && (
            <PDFInfoField
              label="- Couple sélective"
              value={formatValueWithSujetLabel(essence.travaux_couple_selective)}
              layout="row"
            />
          )}
          {isNotEmpty(essence.travaux_other) && (
            <PDFInfoField
              label="- Préservation niche écologique"
              value={formatValueWithSujetLabel(essence.travaux_other)}
              layout="row"
            />
          )}
        </View>
        <View style={pdfStyles.column}>
          <PDFInfoField
            label="Date de la prochaine visite"
            value={
              essence.user_edited_date_pro_visite ? `Le ${formatDate(essence.user_edited_date_pro_visite)}` : undefined
            }
            layout="row"
          />
          <PDFInfoField
            label="Date prévisionnelle des travaux"
            value={essence.user_edited_date_travaux ? `Le ${formatDate(essence.user_edited_date_travaux)}` : undefined}
            layout="row"
          />

          <PDFInfoField label="Commentaires" value={essence.travaux_com} layout="row" />
        </View>
      </View>
    </View>
  );
}

interface EpaysageAltPDFDocumentProps {
  data: EpaysageAltPrintDataFields;
  epaysageId: number;
  essences: any[];
}

// Content component that returns only Pages (without Document wrapper)
// Used for multi-print where multiple documents are combined in a single Document
export const EpaysageAltPDFDocumentContent: React.FC<EpaysageAltPDFDocumentProps> = ({
  data,
  epaysageId,
  essences = [],
}) => {

  return (
    <>
      {/* Page 1 - Main Epaysage Info */}
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <PDFHeader
          organizationName={data.organizationName}
          organizationAddress={data.organizationAddress}
          organizationCity={data.organizationCity}
          organizationZipCode={data.organizationZipCode}
          organizationSiret={data.organizationSiret}
          organizationNumCertification={data.organizationNumCertification}
          organizationLogo={data.organizationLogo}
          user={data.user}
          logoSize="small"
          marginBottom={10}
        />

        {/* Main Title */}
        <PDFSectionHeader
          title={`FICHE DE SYNTHESE ESPACE BOISE - ${epaysageId}`}
          variant="epaysage"
        />

        {/* Epaysage Identification */}
        <View style={pdfStyles.grid4}>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Date" value={data.formattedDate} layout="row" />
            <PDFInfoField label="Type" value={data.type_espace} layout="row" />
            <PDFInfoField label="Nbr d'Arbres Total" value={data.totalSubjects} layout="row" />
          </View>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Code" value={data.code_site} layout="row" />
            <PDFInfoField label="Identification" value={data.num_sujet} layout="row" />
          </View>
          <View style={pdfStyles.columnNoLeft}>
            {data.essences[0] && <PDFInfoField label="Essence 1" value={data.essences[0]} layout="row" />}
            {data.essences[1] && <PDFInfoField label="Essence 2" value={data.essences[1]} layout="row" />}
            {data.essences[2] && <PDFInfoField label="Essence 3" value={data.essences[2]} layout="row" />}
          </View>
          <View style={pdfStyles.columnNoLeft}>
            {data.essences[3] && <PDFInfoField label="Essence 4" value={data.essences[3]} layout="row" />}
            {data.essences[4] && <PDFInfoField label="Essence 5" value={data.essences[4]} layout="row" />}
            <PDFInfoField label="Surface Totale" value={data.surface} layout="row" />
          </View>
        </View>

        <PDFInfoField label="Adresse" value={data.addressLine} layout="row" />

        {/* Caractéristiques */}
        <PDFSectionHeader title="Caractéristiques" variant="secondary" />
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Milieu" value={data.caracteristiques.milieu} layout="row" />
            <PDFInfoField label="Sol" value={data.caracteristiques.sol} layout="row" />
            <PDFInfoField label="Nuisance" value={data.environnement.nuisance} layout="row" />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Situation" value={data.environnement.situation} layout="row" />
            <PDFInfoField label="Domaine" value={data.caracteristiques.domaine} layout="row" />
            <PDFInfoField label="Cohabitation" value={data.caracteristiques.proximite} layout="row" />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Accès" value={data.caracteristiques.accessibilite} layout="row" />
            <PDFInfoField label="Fréquentation" value={data.caracteristiques.taux_freq} layout="row" />
            <PDFInfoField label="Type de Passage" value={data.caracteristiques.type_passage} layout="row" />
          </View>
        </View>

        <View style={pdfStyles.grid2}>
          <View style={pdfStyles.infoFieldRow}>
            <Text style={pdfStyles.infoLabel}>Risque global de l'espace boisé : </Text>
            <PDFRiskIndicator risk={data.risque.general} showLabel={true} size="small" />
          </View>
          <PDFInfoField label="Age moyen estimé du boisement" value={data.caracteristiques.age_estime} layout="row" />
        </View>

        {/* Images */}
        {(data.img1 || data.img2 || data.img3) && (
          <View style={pdfStyles.grid3}>
            {data.img1 && (
              <View style={pdfStyles.photoContainerSmall}>
                <Image src={convertImageUrlForPDF(data.img1, { width: 700 })} style={pdfStyles.photoSmall} />
              </View>
            )}
            {data.img2 && (
              <View style={pdfStyles.photoContainerSmall}>
                <Image src={convertImageUrlForPDF(data.img2, { width: 700 })} style={pdfStyles.photoSmall} />
              </View>
            )}
            {data.img3 && (
              <View style={pdfStyles.photoContainerSmall}>
                <Image src={convertImageUrlForPDF(data.img3, { width: 700 })} style={pdfStyles.photoSmall} />
              </View>
            )}
          </View>
        )}

        {/* Comments */}
        <View style={pdfStyles.commentsWithBorder}>
          <Text style={pdfStyles.commentsLabel}>Commentaire général de l'Espace Boisé : </Text>
          <Text style={pdfStyles.commentsValueNoBorder}>{data.comments}</Text>
        </View>
      </Page>

      {/* Pages for Essences */}
      {essences.map((essence, index) => {
        const sectionTitle = `Essence ${index + 1}`;
        const formattedEssenceName = data.essences[index] || sectionTitle;

        return (
          <Page key={`essence-${index}`} size="A4" style={pdfStyles.page}>
            <EssencePDFSection
              essence={essence}
              essenceIndex={index}
              sectionTitle={sectionTitle}
              essenceName={formattedEssenceName}
            />
          </Page>
        );
      })}
    </>
  );
};

// Full Document component (wraps Content in Document)
export const EpaysageAltPDFDocument: React.FC<EpaysageAltPDFDocumentProps> = (props) => {
  return (
    <Document>
      <EpaysageAltPDFDocumentContent {...props} />
    </Document>
  );
};
